import { useEffect, useState } from "react";
import { Banner, Card, SectionTitle } from "../components/ui";
import type { Api } from "../lib/api";
import type { LicenseSummary } from "../lib/types";

export function Licenses({ api }: { api: Api }) {
  const [licenses, setLicenses] = useState<LicenseSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    api
      .listMyLicenses()
      .then((r) => setLicenses(r.licenses))
      .catch(() => setError("Could not load your licenses."));
  }, [api]);

  function saveBlob(filename: string, content: string) {
    const blob = new Blob([content], { type: "application/octet-stream" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  async function download(license: LicenseSummary, certificate: boolean) {
    setBusyId(license.id);
    setError(null);
    try {
      if (certificate) {
        const { certificate: content, filename } = await api.downloadMyLicenseCertificate(license.id);
        saveBlob(filename, content);
      } else {
        const { pem, filename } = await api.downloadMyLicense(license.id);
        saveBlob(filename, pem);
      }
    } catch {
      setError(certificate ? "Certificate download failed." : "License download failed.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div>
      <SectionTitle
        kicker="Licenses · Entitlements"
        title="Licenses"
        desc="Your account's issued license files and certificates."
      />

      {error && (
        <div className="mb-6">
          <Banner tone="error">{error}</Banner>
        </div>
      )}

      {!licenses && !error && <p className="text-sm text-ink-muted">Loading licenses…</p>}
      {licenses && licenses.length === 0 && (
        <Card>
          <p className="text-sm text-ink-muted">No licenses have been issued to your account yet.</p>
        </Card>
      )}

      <div className="grid gap-4">
        {licenses?.map((license) => (
          <Card key={license.id}>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-lg font-semibold text-ink">{license.customerName}</span>
                  <span className="badge-accent">
                    {license.type === "hub" ? "Hub" : "Named user"}
                  </span>
                  <span className="text-xs text-ink-muted">
                    {license.count} × {license.termYears}y
                  </span>
                  {license.status === "revoked" && <span className="badge text-danger">Revoked</span>}
                  {new Date(license.activationEnd).getTime() < Date.now() && (
                    <span className="badge text-danger">Expired</span>
                  )}
                </div>
                <div className="mt-1 text-sm text-ink-dim">
                  {license.productSku} · {new Date(license.activationStart).toLocaleDateString()} →{" "}
                  {new Date(license.activationEnd).toLocaleDateString()}
                </div>
              </div>
              {license.status !== "revoked" && (
                <div className="flex shrink-0 flex-wrap items-center gap-2">
                  <button
                    className="btn-primary"
                    disabled={busyId === license.id}
                    onClick={() => void download(license, false)}
                  >
                    {busyId === license.id ? "Preparing…" : "Download"}
                  </button>
                  <button
                    className="btn-ghost"
                    disabled={busyId === license.id}
                    onClick={() => void download(license, true)}
                  >
                    Certificate
                  </button>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
